import React, { useState, useEffect, useCallback } from 'react';
import { getHostelAdmissionsPortal, getHostelPaymentsPortal } from '../../api';
import { FiCalendar, FiCreditCard, FiDollarSign } from 'react-icons/fi';

export default function HostelFinancesPortal() {
  const [admissions, setAdmissions] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('residents');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [aRes, pRes] = await Promise.all([getHostelAdmissionsPortal(), getHostelPaymentsPortal()]);
      setAdmissions(aRes.data);
      setPayments(pRes.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const totalDues = admissions.reduce((s, a) => s + (Number(a.total_dues) || 0), 0);
  const totalPaid = admissions.reduce((s, a) => s + (Number(a.total_paid) || 0), 0);
  const totalBalance = totalDues - totalPaid;

  const tabStyle = (tab) => ({
    padding: '7px 18px', border: '1px solid rgba(96,165,250,0.3)', borderRadius: '6px', cursor: 'pointer',
    background: activeTab === tab ? '#0e1e45' : 'transparent',
    color: activeTab === tab ? '#fff' : '#f5bc1f',
    fontWeight: '600', fontSize: '13px'
  });

  const cardStyle = (color) => ({
    background: `${color}10`, border: `1px solid ${color}33`, borderRadius: '10px', padding: '18px', textAlign: 'center'
  });

  return (
    <div>
      <div style={{ marginBottom: '20px' }}>
        <h2 style={{ color: '#fff', margin: 0, fontSize: '20px' }}><FiDollarSign /> Finances</h2>
        <p style={{ color: '#64748b', margin: '4px 0 0', fontSize: '13px' }}>Dues, payments and outstanding balances</p>
      </div>

      {/* Summary Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '12px', marginBottom: '24px' }}>
        <div style={cardStyle('#3b82f6')}>
          <div style={{ fontSize: '20px', fontWeight: '700', color: '#f5bc1f' }}>{admissions.length}</div>
          <div style={{ fontSize: '11px', color: '#64748b', marginTop: '3px' }}>Total Residents</div>
        </div>
        <div style={cardStyle('#f59e0b')}>
          <div style={{ fontSize: '18px', fontWeight: '700', color: '#fcd34d' }}>Rs. {totalDues.toLocaleString()}</div>
          <div style={{ fontSize: '11px', color: '#64748b', marginTop: '3px' }}>Total Dues</div>
        </div>
        <div style={cardStyle('#10b981')}>
          <div style={{ fontSize: '18px', fontWeight: '700', color: '#34d399' }}>Rs. {totalPaid.toLocaleString()}</div>
          <div style={{ fontSize: '11px', color: '#64748b', marginTop: '3px' }}>Total Received</div>
        </div>
        <div style={cardStyle(totalBalance > 0 ? '#ef4444' : '#10b981')}>
          <div style={{ fontSize: '18px', fontWeight: '700', color: totalBalance > 0 ? '#f87171' : '#34d399' }}>Rs. {totalBalance.toLocaleString()}</div>
          <div style={{ fontSize: '11px', color: '#64748b', marginTop: '3px' }}>Balance Outstanding</div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
        <button style={tabStyle('residents')} onClick={() => setActiveTab('residents')}>Per Resident</button>
        <button style={tabStyle('payments')} onClick={() => setActiveTab('payments')}>Payment History</button>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '30px' }}>
          <div style={{ width: '28px', height: '28px', border: '3px solid rgba(255,255,255,0.1)', borderTop: '3px solid #f5bc1f', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto' }}></div>
        </div>
      ) : activeTab === 'residents' ? (
        <div style={{ display: 'grid', gap: '10px' }}>
          {admissions.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '30px', color: '#475569' }}>No residents found.</div>
          ) : admissions.map(a => (
            <div key={a.id} style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '10px', padding: '14px 18px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
                <div>
                  <h4 style={{ color: '#e2e8f0', margin: '0 0 6px', fontSize: '14px' }}>{a.full_name}</h4>
                  <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', fontSize: '12px', color: '#64748b' }}>
                    <span><FiCalendar /> {a.date_of_admission ? new Date(a.date_of_admission).toLocaleDateString('en-PK') : '—'}</span>
                    <span>⏱️ {a.months_elapsed || 0} months</span>
                    <span><FiCreditCard /> Rs. {Number(a.monthly_rent || 0).toLocaleString()}/mo</span>
                    <span style={{ color: a.status === 'active' ? '#34d399' : '#9ca3af' }}>● {a.status}</span>
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, auto)', gap: '16px', alignItems: 'center' }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '14px', fontWeight: '700', color: '#fcd34d' }}>Rs. {Number(a.total_dues || 0).toLocaleString()}</div>
                    <div style={{ fontSize: '10px', color: '#64748b' }}>Dues</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '14px', fontWeight: '700', color: '#34d399' }}>Rs. {Number(a.total_paid || 0).toLocaleString()}</div>
                    <div style={{ fontSize: '10px', color: '#64748b' }}>Paid</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '14px', fontWeight: '700', color: (a.balance || 0) > 0 ? '#f87171' : '#34d399' }}>Rs. {Number(a.balance || 0).toLocaleString()}</div>
                    <div style={{ fontSize: '10px', color: '#64748b' }}>Balance</div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        /* Payment History */
        <div style={{ display: 'grid', gap: '10px' }}>
          {payments.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '30px', color: '#475569' }}>No payment records found.</div>
          ) : payments.map((p, i) => (
            <div key={p.id} style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '8px', padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
              <div>
                <div style={{ color: '#e2e8f0', fontSize: '14px', fontWeight: '500' }}>{p.full_name}</div>
                <div style={{ color: '#64748b', fontSize: '12px' }}>
                  {p.payment_date ? new Date(p.payment_date).toLocaleDateString('en-PK') : '—'}
                  {p.months_covered && ` · ${p.months_covered} month(s)`}
                  {p.notes && ` · ${p.notes}`}
                </div>
              </div>
              <div style={{ fontSize: '16px', fontWeight: '700', color: '#34d399' }}>
                Rs. {Number(p.amount).toLocaleString()}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
